import {DefaultValues} from '@maporium-workspace/shared';
import { NodeSingular } from 'cytoscape';
import {SettingsService} from '../api/services/setting.service';
import {MapService} from '../api/services/map.service';
import chroma from 'chroma-js';
import { darkenWithHSL } from '../shared/helpers/color-helpers';
// eslint-disable-next-line @typescript-eslint/no-var-requires

export const cyNodeStyles: any[] = [
  {
    selector: '.label-expanded',
    style: {
      'color': '#000',
    }
  },
  {
    selector: 'node.highlighted',
    style: {
      //@ts-ignore
      'overlay-opacity': () => SettingsService.sharedValues.node.nodeDropOverlayOpacity,
      'overlay-padding': '2px',
      'overlay-color': () => SettingsService.sharedValues.node.nodeHighlightColor,
      'z-index-compare': 'manual',
      'z-index': 2
    }
  },
  {
    selector: 'node:parent.grabbed',
    style: {
      opacity: () => SettingsService.sharedValues.node.nodeDragOpacity,
      'z-index-compare': 'manual',
      'z-index': 2
    }
  },
  {
    selector: '.grabbed',
    style: {
      'overlay-opacity': () => SettingsService.sharedValues.node.nodeDragOpacity,
      'overlay-padding': '2px',
      'z-index': 2,
      'z-index-compare': 'manual'
    }
  },
  {
    selector: '.collapsed',
    style: {
      'height': (node: NodeSingular) => {
        let scale = node.data('properties')?.scale;
        scale = (scale === 0 || scale === '0') ? 0.25 : scale;
        const type = node.data('type');
        switch (type) {
          case 'PENTAGON': {
            return scale ? scale * 8 : 10;
          }
          case 'HEXAGON': {
            return scale ? scale * 8 : 10;
          }
          case 'HEPTAGON': {
            return scale ? scale * 9 : 10;
          }
          default: {
            return scale ? scale * 10 : 10;
          }
        }
      },
    }
  },
  {
    selector: 'node.hidden',
    style: {
      'visibility': 'hidden',
      'opacity': 0
    }
  },
  {
    selector: 'node.display-none',
    style: {
      'display': 'none'
    }
  },
  {
    selector: 'edge.display-none',
    style: {
      'display': 'none'
    }
  }

];

/**
 * A compound parent's `opacity` multiplies into its children, so a dimmed node
 * inside a dimmed container would fade twice. Dimming therefore goes through
 * the per-element opacities, which do not cascade, and this factor folds into
 * whatever opacity the node itself is configured with.
 */
export function nodeDimFactor(node: NodeSingular): number {
  if (!node.hasClass('dimmed') || node.hasClass('highlighted')) return 1;
  return SettingsService.sharedValues.filteringOpacity;
}

export const miscStyles: any[] = [
  {
    selector: '.eh-hover',
    style: {
      'background-color': (node: NodeSingular) => {
        // Use HSL transformation for background color
        return darkenWithHSL(node.data('color') || DefaultValues.node.color, 0.5);
      },
      'width': '1',
      'source-arrow-shape': 'none',
      'z-index-compare': 'manual',
      'z-index': 5
    }
  },

  // eh-source and eh-target are styles that apply on target node and source node during new link creation
  {
    selector: '.eh-source',
    style: {
      'border-width': 2,
      'border-color': (node: NodeSingular) => {
        // Use HSL transformation for border color
        return darkenWithHSL(node.data('color') || DefaultValues.node.color, 0.5);
      },
      'source-arrow-shape': 'none',
      'z-index-compare': 'manual',
      'z-index': 5
    }
  },

  {
    selector: '.eh-target',
    style: {
      'border-width': 2,
      'border-color': (node: NodeSingular) => {
        // Use HSL transformation for border color with a softer darken amount
        return darkenWithHSL(node.data('color') || DefaultValues.node.color, 0.3);
      },
      'source-arrow-shape': 'none',
      'z-index-compare': 'manual',
      'z-index': 5
    }
  },
  {
    selector: '.opaque',
    style: {
      'opacity': 0.5
    }
  },
  {
    selector: '.visible',
    style: {
      'opacity': 1
    }
  },
  {
    selector: '.hidden',
    style: {
      'opacity': 0
    }
  },
  {
    selector: 'edge.hidden',
    style: {
      'visibility': 'hidden',
    }
  },
  {
    selector: '.hidden.highlighted',
    style: {
      'opacity': 1
    }
  },
  /**
   * MAP-1017: "Other links dimmed" - unlike `.hidden`, this does NOT set
   * visibility:hidden, so the edge stays interactive/hit-testable (click AND
   * selection-rectangle) while rendering at the configured filtering opacity.
   */
  {
    selector: 'edge.dimmed',
    style: {
      //@ts-ignore
      'opacity': () => SettingsService.sharedValues.filteringOpacity,
    }
  },
  {
    selector: '.dimmed.highlighted',
    style: {
      'opacity': 1
    }
  },
  {
    selector: 'node.dimmed',
    style: {
      //@ts-ignore
      'border-opacity': (node: NodeSingular) => nodeDimFactor(node),
      //@ts-ignore
      'text-opacity': (node: NodeSingular) => nodeDimFactor(node),
    }
  },
  {
    selector: '.unlabeled',
    style: {
      'text-opacity': 0
    }
  },
];


export const cyStyles = [
  ...cyNodeStyles,
  ...miscStyles,
];


export const StatusIcons = {
  HAS_META: '\ue836',
  HAS_COMMENTS: '\ue039',
  HAS_LINKS: '\ue03a',
}
